import { getRequest, postRequest, URL } from "shared/utils";
import * as types from "./actionTypes";

const queryParms = (request) => {
  const {
    clientId,
    pageNumber,
    pageSize,
    sortColumn,
    sortDirection,
    searchKeyword,
    noticeDateStart,
    noticeDateEnd,
    filters,
  } = request;

  let parms = `?clientId=${clientId}&pageNumber=${pageNumber}&pageSize=${pageSize}`;

  if (sortColumn) {
    parms += `&sortColumn=${sortColumn}&sortDirection=${sortDirection}`;
  }

  if (searchKeyword) {
    parms += `&searchKeyword=${searchKeyword}`;
  }

  if (noticeDateStart && noticeDateEnd) {
    parms += `&noticeDateStart=${noticeDateStart}&noticeDateEnd=${noticeDateEnd}`;
  }

  if (filters) {
    const dateRangeKeys = ["leaseStartDate", "leaseEndDate"];

    Object.keys(filters).forEach((key) => {
      if (dateRangeKeys.includes(key)) {
        const dr = filters[key];
        if (dr) {
          if (dr.dateType !== null && dr.dateType !== undefined) {
            const labelMap = {
              leaseStartDate: "lease start date",
              leaseEndDate: "lease end date",
            };
            const label = labelMap[key];
            parms += `&dateFilterType[${label}]=${dr.dateType}`;
          }
          if (dr.start) {
            const startDate = dr.start.substring(0, 10);
            parms += `&${key}.start=${startDate}`;
          }
          if (dr.end) {
            const endDate = dr.end.substring(0, 10);
            parms += `&${key}.end=${endDate}`;
          }
        }
        return;
      }

      var filterBy = filters[key];
      if (filterBy && filterBy.length > 0) {
        filterBy.forEach((f) => {
          parms += `&${key}=${f}`;
        });
      }
    });
  }

  return parms;
};

export function getLeases(requestWithParams) {
  const url = `${URL.lease}/v2${queryParms(requestWithParams)}`;

  const request = getRequest(url);

  return {
    type: types.GET_LEASES,
    payload: request,
  };
}

const buildDateFilterBody = (filters) => {
  const dateRangeKeys = ["leaseStartDate", "leaseEndDate"];
  const labelMap = {
    leaseStartDate: "lease start date",
    leaseEndDate: "lease end date",
  };
  const result = { dateFilterType: {} };

  dateRangeKeys.forEach((key) => {
    const dr = filters?.[key];
    if (!dr) return;

    if (dr.dateType !== null && dr.dateType !== undefined) {
      result.dateFilterType[labelMap[key]] = dr.dateType;
    }
    if (dr.start) result[`${key}.start`] = dr.start.substring(0, 10);
    if (dr.end) result[`${key}.end`] = dr.end.substring(0, 10);
  });

  return result;
};

export function getLeaseSpreadsheet(parms) {
  var utcOffset = new Date().getTimezoneOffset() / 60;

  utcOffset = utcOffset * -1;

  let url = `${URL.lease}/spreadsheet`;

  const { filters, columns, ...rest } = parms;

  const cleanFilters = { ...filters };
  for (var prop in cleanFilters) {
    if (Array.isArray(cleanFilters[prop]) && cleanFilters[prop].length === 0)
      delete cleanFilters[prop];
  }

  // Remove date range keys from filters before spreading — handled separately
  const dateRangeKeys = ["leaseStartDate", "leaseEndDate"];
  dateRangeKeys.forEach((k) => delete cleanFilters[k]);

  const dateFilterBody = buildDateFilterBody(filters);

  const request = postRequest(
    url,
    { ...rest, ...cleanFilters, columns, utcOffset, ...dateFilterBody },
    false,
    "blob"
  );

  return {
    type: types.GET_LEASE_SPREADSHEET,
    payload: request,
  };
}

export function getLease(id) {
  const url = `${URL.lease}/${id}`;

  const request = getRequest(url);

  return {
    type: types.GET_LEASE,
    payload: request,
  };
}

export function getLeaseFilterOptions(clientId) {
  const url = `${URL.lease}/filterOptions?clientId=${clientId}`;

  const request = getRequest(url);

  return {
    type: types.GET_LEASE_FILTER_OPTIONS,
    payload: request,
  };
}

export function getLeaseCities(clientId) {
  const url = `${URL.lease}/cities?clientId=${clientId}`;

  const request = getRequest(url);

  return {
    type: types.GET_LEASE_CITIES,
    payload: request,
  };
}

export function getDistinctLeaseRegions(clientId) {
  const url = `${URL.lease}/regions?clientId=${clientId}`;

  const request = getRequest(url);

  return {
    type: types.GET_DISTINCT_LEASE_REGIONS,
    payload: request,
  };
}

export function getLeaseStateProvinces(clientId) {
  const url = `${URL.lease}/states?clientId=${clientId}`;

  const request = getRequest(url);

  return {
    type: types.GET_LEASE_STATE_PROVINCES,
    payload: request,
  };
}

export function getLeaseCountries(clientId) {
  const url = `${URL.lease}/countries?clientId=${clientId}`;

  const request = getRequest(url);

  return {
    type: types.GET_LEASE_COUNTRIES,
    payload: request,
  };
}

export function getLeaseTypes(clientId) {
  const url = `${URL.lease}/types?clientId=${clientId}`;

  const request = getRequest(url);

  return {
    type: types.GET_LEASE_TYPES,
    payload: request,
  };
}

export function getLeaseStatuses(clientId) {
  const url = `${URL.lease}/statuses?clientId=${clientId}`;

  const request = getRequest(url);

  return {
    type: types.GET_LEASE_STATUSES,
    payload: request,
  };
}

export function getLeaseEventTypes(clientId) {
  const url = `${URL.lease}/eventTypes?clientId=${clientId}`;

  const request = getRequest(url);

  return {
    type: types.GET_LEASE_EVENT_TYPES,
    payload: request,
  };
}

export function getLeaseTenureTypes(clientId) {
  const url = `${URL.lease}/tenureTypes?clientId=${clientId}`;

  const request = getRequest(url);

  return {
    type: types.GET_TENURE_TYPES,
    payload: request,
  };
}

export function getRelatedLeaseEvents(leaseId) {
  const url = `${URL.lease}/${leaseId}/leaseEvents`;

  const request = getRequest(url);

  return {
    type: types.GET_RELATED_LEASE_EVENTS,
    payload: request,
  };
}

export function getAllLeases(requestWithParams) {
  const url = `${URL.lease}/v2${queryParms(requestWithParams)}`;

  const request = getRequest(url);

  return {
    type: types.GET_ALL_LEASES,
    payload: request,
  };
}

export function getLeasePageSize(userId, tableName) {
  const url = `${URL.lease}/pageSize?userId=${userId}&tableName=${tableName}`;

  const request = getRequest(url);

  return {
    type: types.GET_LEASE_PAGE_SIZE,
    payload: request,
  };
}
